import * as atlas from 'azure-maps-control'
import {
  defaultCoordinates, azureMapsApiKey, PolygonStyle,
  zoomToFeature
} from './MapHelpers'
import { getLegendInstance } from '@/utils/map/Legend'
import { AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { createPolygonLayersForDataSource, disposeMap } from '@/utils/map/MapHelpers'

const DEFAULT_ZOOM = 13
const DEFAULT_STYLE = 'satellite_road_labels'
const DEFAULT_VIEW = 'Auto'
const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels

class Map {
  map
  defaultZoom

  dispose = () => {
    disposeMap(this.map)
  }

  constructor ({
    mapHtmlId,
    latitude = defaultCoordinates.latitude,
    longitude = defaultCoordinates.longitude,
    zoom = DEFAULT_ZOOM,
    style = DEFAULT_STYLE
  }) {
    this.coords = { latitude, longitude }
    this.defaultZoom = zoom
    const mapOptions = {
      center: [longitude, latitude],
      zoom: zoom,
      view: DEFAULT_VIEW,
      style: style,
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey: azureMapsApiKey()
      }
    }

    this.map = new atlas.Map(mapHtmlId, mapOptions)
  }

  zoom (options) {
    this.map.setCamera({
      center: [this.coords.longitude, this.coords.latitude],
      zoom: this.defaultZoom,
      type: 'fly',
      ...options
    })
  }
}

class MapWithLegend extends Map {
  constructor ({
    angelGeojson,
    onReady,
    mapHtmlId,
    pickupRadius,
    latitude = defaultCoordinates.latitude,
    longitude = defaultCoordinates.longitude,
    zoom = DEFAULT_ZOOM,
    style = DEFAULT_STYLE
  }) {
    super({ mapHtmlId, latitude, longitude, zoom, style })
    this.angelGeojson = new AngelGeojson(angelGeojson)
    this.pickupRadius = pickupRadius
    this.initLegend()
    this.map.events.add('ready', () => {
      this.initCut()
      this.initOrUpdateParcel()
      this.initPickupRadius(longitude, latitude, pickupRadius)
      this.zoom()
      onReady()
    })
  }

  initLegend = () => {
    this.legend = getLegendInstance()
    this.legend.viewingCut = this.angelGeojson.layerIsNotEmpty(CUT)
    this.legend.viewingParcel = this.angelGeojson.layerIsNotEmpty(PARCELS)
    this.legend.viewingPickupRadius = !!this.pickupRadius
  }

  initCut = () => {
    this.cutSource = new atlas.source.DataSource()
    this.map.sources.add(this.cutSource)
    if (this.angelGeojson.layerIsNotEmpty(CUT)) {
      this.cutSource.setShapes(this.angelGeojson.getLayer(CUT))
      this.cutLayers = createPolygonLayersForDataSource(this.cutSource, PolygonStyle.Cut)
      this.map.layers.add(this.cutLayers, 'labels')
    }
  }

  initOrUpdateParcel = () => {
    if (!this.parcelSource) {
      this.createParcelSource()
    }
    this.setParcelShapes()
    if (!this.parcelLayers) {
      this.createParcelLayers()
    }
  }

  createParcelSource = () => {
    this.parcelSource = new atlas.source.DataSource()
    this.map.sources.add(this.parcelSource)
  }

  setParcelShapes = () => {
    if (this.angelGeojson.layerIsNotEmpty(PARCELS)) {
      this.parcelSource.setShapes(this.angelGeojson.getLayer(PARCELS))
    }
  }

  createParcelLayers = () => {
    this.parcelLayers = createPolygonLayersForDataSource(this.parcelSource, PolygonStyle.Parcels)
    this.map.layers.add(this.parcelLayers, 'labels')
  }

  initPickupRadius = (lng, lat, radius) => {
    if (this.pickupRadius) {
      this.pickupRadiusSource = new atlas.source.DataSource()
      this.map.sources.add(this.pickupRadiusSource)
      const position = [lng, lat]
      const point = new atlas.data.Point(position)
      this.pickupRadiusShape = new atlas.Shape(point, null, {
        subType: 'Circle',
        radius: radius
      })
      this.pickupRadiusSource.add(this.pickupRadiusShape)
      this.pickupRadiusLayers = createPolygonLayersForDataSource(this.pickupRadiusSource, PolygonStyle.PickupRadius)
      this.map.layers.add(this.pickupRadiusLayers, 'labels')
    }
  }

  setCoords (lat, lng) {
    this.coords.latitude = lat
    this.coords.longitude = lng
  }

  setPickupRadius = (radius) => {
    if (this.pickupRadiusShape) {
      this.pickupRadiusShape.setProperties({
        ...this.pickupRadiusShape.getProperties(),
        radius
      })
    }
  }

  movePickupRadius = ({ latitude, longitude }) => {
    if (this.pickupRadiusShape) {
      const p = atlas.data.Position.fromLatLng([longitude, latitude])
      this.pickupRadiusShape.setCoordinates(p)
    }
  }

  setAngelGeojson = (angelGeojson) => {
    this.angelGeojson = angelGeojson
    if (angelGeojson.layerIsNotEmpty(PARCELS)) {
      this.initOrUpdateParcel()
      this.zoom()
      this.legend.viewingParcel = true
      if (this.parcelLayers) this.parcelLayers.forEach(l => this.setLayerVisibility(l, true))
    } else {
      this.legend.viewingParcel = false
      if (this.parcelLayers) this.parcelLayers.forEach(l => this.setLayerVisibility(l, false))
    }
  }

  removeLayer = (layer) => {
    switch (layer) {
      case CUT: {
        this.cutSource.clear()
        this.angelGeojson.setLayer(CUT, null)
        this.legend.viewingCut = false
        break
      }
      case PARCELS: {
        this.parcelSource.clear()
        this.angelGeojson.setLayer(PARCELS, null)
        this.legend.viewingParcel = false
      }
    }
  }

  zoom = (options = null) => {
    if (this.angelGeojson.layerIsNotEmpty(CUT)) {
      this.zoomCut(options)
    } else if (this.angelGeojson.layerIsNotEmpty(PARCELS)) {
      this.zoomParcel(options)
    } else {
      this.zoomCoordinate(options)
    }
  }

  zoomCut = (options = null) => {
    zoomToFeature(this.angelGeojson.getLayer(CUT), this.map, options)
  }

  zoomParcel = (options = null) => {
    zoomToFeature(this.angelGeojson.getLayer(PARCELS), this.map, options)
  }

  zoomCoordinate = (options = null) => {
    super.zoom(options)
  }

  toggleLegendItemVisibility = (legendItem) => {
    legendItem.hidden = !legendItem.hidden
    let layers
    switch (legendItem.label) {
      case 'parcel': {
        layers = this.parcelLayers
        break
      }
      case 'pickupRadius': {
        layers = this.pickupRadiusLayers
        break
      }
      case 'tractCut': {
        layers = this.cutLayers
        break
      }
      default: layers = []
    }
    layers.forEach(layer => this.setLayerVisibility(layer, !legendItem.hidden))
  }

  setLayerVisibility = (layer, isVisible) => {
    const options = layer.getOptions()
    options.visible = isVisible
    layer.setOptions(options)
  }
}

export function getMapWithLegendInstance ({
  angelGeojson, onReady, mapHtmlId, pickupRadius,
  latitude = defaultCoordinates.latitude, longitude = defaultCoordinates.longitude,
  zoom = DEFAULT_ZOOM, style = DEFAULT_STYLE
}) {
  return new MapWithLegend({
    angelGeojson,
    onReady,
    mapHtmlId,
    pickupRadius,
    latitude,
    longitude,
    zoom,
    style
  })
}
